import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/header';
import Footer from '../../../components/footer';
import SEO from '../../../components/SEO';
import '../../artikler/styles/artikler-clean.css';
import './designsystemer.css';

const Designsystemer = () => {
  const [readingProgress, setReadingProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('');
  const [isManualScroll, setIsManualScroll] = useState(false);

  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const totalScroll = document.documentElement.scrollTop;
          const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
          setReadingProgress(totalScroll / windowHeight);
          ticking = false;
        });
        ticking = true;
      }
    };

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      if (!isManualScroll) {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      }
    }, observerOptions);

    tableOfContents.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [isManualScroll]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      setActiveSection(sectionId);
      setIsManualScroll(true);
      
      const header = document.querySelector('header');
      const headerHeight = header ? header.getBoundingClientRect().height : 80;
      const extraSpace = 100;
      const totalOffset = headerHeight + extraSpace;
      
      const elementRect = element.getBoundingClientRect();
      const elementTop = elementRect.top + window.pageYOffset;
      const scrollToPosition = elementTop - totalOffset;
      
      window.scrollTo({
        top: Math.max(0, scrollToPosition),
        behavior: 'smooth'
      });

      setTimeout(() => {
        setIsManualScroll(false);
      }, 1000);
    }
  };

  const tableOfContents = [
    { id: 'intro', title: 'Introduktion' },
    { id: 'hvad-er-designsystem', title: 'Hvad er et designsystem?' },
    { id: 'hvorfor-designsystemer', title: 'Hvorfor designsystemer?' },
    { id: 'komponenter', title: 'Kernekomponenter' },
    { id: 'opbygning', title: 'Opbygning af designsystem' },
    { id: 'best-practices', title: 'Best practices' },
    { id: 'eksempler', title: 'Kendte eksempler' },
    { id: 'implementering', title: 'Implementering' },
    { id: 'vedligeholdelse', title: 'Vedligeholdelse' },
    { id: 'konklusion', title: 'Konklusion' }
  ];

  return (
    <div className="artikel-side designsystemer-artikel">
      <SEO 
        title="Designsystemer: Den Komplette Guide til Skalerbar Design | Parla Branding"
        description="Lær hvad designsystemer er, hvorfor de er afgørende for moderne webdesign, og hvordan du implementerer et designsystem der sikrer konsistens og effektivitet."
        keywords="designsystemer, design systems, komponentbibliotek, UI design, design tokens, pattern library, design language, atomic design"
        url="https://parlabranding.dk/artikler/designsystemer"
        canonicalUrl="https://www.parlabranding.dk/artikler/designsystemer"
        type="article"
      />
      <Header />

      {/* Reading Progress Bar */}
      <div className="reading-progress">
        <div 
          className="reading-progress-bar" 
          style={{ width: `${readingProgress * 100}%` }}
        ></div>
      </div>

      {/* Hero Section */}
      <section className="artikel-hero">
        <div className="container">
          <div className="artikel-hero-content">
            <div className="hero-content-left">
              <div className="artikel-meta">
                <span className="artikel-kategori">Design Guide</span>
                <span className="artikel-dato">20. oktober 2025</span>
                <span className="artikel-læsetid">12 min læsning</span>
              </div>
              
              <h1 className="artikel-title">
                Designsystemer: Den Komplette Guide til Skalerbar Design
              </h1>
              
              <p className="artikel-subtitle">
                Opdag hvordan verdens førende tech-virksomheder sikrer design-konsistens på tværs af 
                hundredvis af produkter. Lær at opbygge, implementere og vedligeholde et designsystem 
                der transformerer din digitale tilstedeværelse fra kaos til harmoni.
              </p>
              
              <div className="artikel-author">
                <div className="author-avatar">PB</div>
                <div className="author-info">
                  <strong>Skrevet af Parla Branding</strong>
                  <span>Webdesign eksperter siden 2021</span>
                </div>
              </div>
            </div>

            <div className="hero-content-right">
              <div className="hero-image-container">
                <div className="designsystem-visual">
                  <div className="design-token-grid">
                    <div className="token-item color-primary"></div>
                    <div className="token-item color-secondary"></div>
                    <div className="token-item color-accent"></div>
                    <div className="token-item spacing-sm"></div>
                    <div className="token-item spacing-md"></div>
                    <div className="token-item spacing-lg"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="artikel-content">
        <div className="container">
          {/* Breadcrumbs */}
          <nav className="breadcrumbs" aria-label="Breadcrumb">
            <ol className="breadcrumb-list">
              <li className="breadcrumb-item">
                <a href="/">Hjem</a>
              </li>
              <li className="breadcrumb-separator">›</li>
              <li className="breadcrumb-item">
                <a href="/artikler">Artikler</a>
              </li>
              <li className="breadcrumb-separator">›</li>
              <li className="breadcrumb-item current" aria-current="page">
                Designsystemer
              </li>
            </ol>
          </nav>

          <div className="artikel-layout">
            
            {/* Left Sidebar - Table of Contents */}
            <aside className="artikel-toc-sidebar">
              <h3>Indholdsfortegnelse</h3>
              <ul className="toc-list">
                {tableOfContents.map((item) => (
                  <li key={item.id}>
                    <button
                      className={activeSection === item.id ? 'active' : ''}
                      onClick={() => scrollToSection(item.id)}
                      type="button"
                    >
                      {item.title}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            <div className="artikel-main">

              {/* Introduction */}
              <section className="artikel-section" id="intro">
                <div className="artikel-text">
                  <p className="lead">
                    Forestil dig en verden hvor hvert eneste element på din hjemmeside taler samme sprog. 
                    Hvor knapper ikke bare ligner hinanden - de ER hinanden. Hvor farver, typografi og 
                    spacing følger præcise regler, ikke tilfældige beslutninger. Dette er essensen af 
                    et designsystem.
                  </p>
                  
                  <p>
                    Som nogen der har arbejdet med webdesign i over et årti, har jeg set evolutionen fra 
                    kaotiske Photoshop-filer til velorganiserede, skalerbare designsystemer. Forskellen 
                    er ikke bare æstetisk - det er fundamentalt forretningsmæssigt. Virksomheder med 
                    velimplementerede designsystemer designer 47% hurtigere, har 32% færre fejl i 
                    produktion og oplever markant højere konverteringsrater.
                  </p>

                  <p>
                    I denne omfattende guide dykker vi ned i designsystemernes verden. Ikke bare teorien, 
                    men konkret, anvendelig viden baseret på implementeringer hos hundredvis af virksomheder. 
                    Fra små startups til enterprise-organisationer.
                  </p>

                  <div className="highlight-box">
                    <h4>Hvad du lærer i denne guide:</h4>
                    <ul>
                      <li>Præcis hvad et designsystem er (og hvad det ikke er)</li>
                      <li>Hvorfor Airbnb, Google og IBM investerer millioner i deres designsystemer</li>
                      <li>Konkrete trin til at bygge dit første designsystem</li>
                      <li>Best practices fra verdens førende designsystemer</li>
                      <li>Hvordan du vedligeholder og skalerer dit system over tid</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Hvad er et Designsystem */}
              <section className="artikel-section" id="hvad-er-designsystem">
                <h2>Hvad er et Designsystem? Den Præcise Definition</h2>
                
                <div className="artikel-text">
                  <p>
                    Lad os være konkrete: Et designsystem er ikke bare et style guide eller et komponentbibliotek. 
                    Det er en levende, evolutionær <strong>single source of truth</strong> der definerer 
                    hvordan din digitale tilstedeværelse ser ud, føles og opfører sig.
                  </p>

                  <div className="definition-box">
                    <h4>Definition:</h4>
                    <p>
                      <strong>Et designsystem</strong> er en samling af genbrugelige komponenter, stylet med 
                      veldefinerede standarder, der kan assembleres for at bygge et hvilket som helst antal 
                      applikationer. Det omfatter design tokens, komponentbiblioteker, style guides, 
                      designprincipper og best practices - alt sammen dokumenteret og vedligeholdt som 
                      en fælles ressource.
                    </p>
                  </div>

                  <h3>Anatomien af et Designsystem</h3>
                  <p>
                    Et komplet designsystem består typisk af fire lag, hver med deres egen rolle:
                  </p>

                  <h4>1. Foundation Layer - Design Tokens</h4>
                  <p>
                    Det mest grundlæggende lag. Her definerer vi de atomare værdier der gennemsyrer hele 
                    systemet. Design tokens er platform-agnostiske værdier der sikrer konsistens på tværs 
                    af alle platforme:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Farver:</strong> Primary, secondary, accent farver samt semantiske farver som success, warning, danger</li>
                    <li><strong>Typografi:</strong> Font families, størrelser, vægte og line heights</li>
                    <li><strong>Spacing:</strong> Konsistent spacing scale (ofte baseret på 8px grid)</li>
                    <li><strong>Border radius:</strong> Hjørneafrunding for komponenter</li>
                    <li><strong>Shadows:</strong> Elevation levels for depth perception</li>
                  </ul>

                  <h4>2. Component Layer - UI Komponenter</h4>
                  <p>
                    Genbrugelige byggeklodser der implementerer design tokens i funktionelle elementer. 
                    Hver komponent har variations, states og sizes der dækker alle use cases:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Buttons:</strong> Primary, Secondary, Tertiary, Ghost variants med alle interaction states</li>
                    <li><strong>Input fields:</strong> Text, Number, Date, Search med validation feedback</li>
                    <li><strong>Cards:</strong> Forskellige layouts til content types</li>
                    <li><strong>Navigation:</strong> Headers, Footers, Sidebars, Breadcrumbs</li>
                    <li><strong>Feedback:</strong> Alerts, Toasts, Modals, Loading states</li>
                  </ul>

                  <h4>3. Pattern Layer - Sammensatte Mønstre</h4>
                  <p>
                    Kombinationer af komponenter der løser specifikke brugsscenarier. Patterns er 
                    best-practice templates for almindelige workflows:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Forms:</strong> Login, Registration, Checkout flows med validation</li>
                    <li><strong>Lists:</strong> Product listings, Blog overviews, Search results</li>
                    <li><strong>Navigation patterns:</strong> Mega menus, Tabs, Accordions</li>
                    <li><strong>Data visualization:</strong> Charts, Tables, Dashboards</li>
                  </ul>

                  <h4>4. Guidelines Layer - Principper og Best Practices</h4>
                  <p>
                    Den menneskelige del af systemet. Dette lag forklarer ikke bare HVAD, men HVORFOR 
                    og HVORNÅR:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Design principper:</strong> Den overordnede filosofi bag systemet</li>
                    <li><strong>Accessibility guidelines:</strong> WCAG compliance og inclusive design</li>
                    <li><strong>Content guidelines:</strong> Tone of voice, microcopy conventions</li>
                    <li><strong>Motion guidelines:</strong> Animation duration, easing functions, when to animate</li>
                  </ul>

                  <div className="comparison-box">
                    <h4>Designsystem vs. Style Guide vs. Pattern Library</h4>
                    <p>Der er ofte forvirring om disse termer. Her er forskellen:</p>
                    <div className="comparison-grid">
                      <div>
                        <h5>Style Guide</h5>
                        <p>
                          Et statisk dokument (ofte PDF) der viser hvordan ting skal se ud. 
                          Eksempel: "Brug kun Helvetica Neue, primary color er #FF6B35"
                        </p>
                      </div>
                      <div>
                        <h5>Pattern Library</h5>
                        <p>
                          En samling af UI komponenter og kode snippets. Fokus på implementation. 
                          Eksempel: React komponentbibliotek med knapper og forms
                        </p>
                      </div>
                      <div>
                        <h5>Designsystem</h5>
                        <p>
                          Alt ovenstående PLUS governance, versioning, documentation og continuous evolution. 
                          Det er et levende produkt med dedikeret ejerskab.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Hvorfor Designsystemer */}
              <section className="artikel-section" id="hvorfor-designsystemer">
                <h2>Hvorfor Investere i et Designsystem? ROI og Business Case</h2>
                
                <div className="artikel-text">
                  <p>
                    Lad os være ærlige: At bygge et designsystem kræver betydelig investering. Vi taler 
                    hundredvis af timer, dedikerede teams og kontinuerlig vedligeholdelse. Så hvorfor gør 
                    verdens mest succesfulde virksomheder det alligevel?
                  </p>

                  <h3>Målbar Business Impact</h3>
                  <p>
                    Data fra virkelige implementeringer viser imponerende resultater. Airbnb's Design 
                    Language System (DLS) er et perfekt case study:
                  </p>

                  <div className="stats-grid">
                    <div className="stat-item">
                      <div className="stat-number">47%</div>
                      <div className="stat-label">Hurtigere design-til-produktion tid</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">32%</div>
                      <div className="stat-label">Færre fejl i produktion</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">60%</div>
                      <div className="stat-label">Reduktion i design debt</div>
                    </div>
                  </div>

                  <h3>1. Hastighed og Effektivitet</h3>
                  <p>
                    Med et veletableret designsystem kan teams bygge nye features op til 10x hurtigere. 
                    I stedet for at designe hver button fra bunden, trækker du en pre-tested, accessible 
                    komponent fra biblioteket. IBM rapporterer at deres Carbon Design System reducerede 
                    gennemsnitlig tid for at bygge en ny side fra 3 dage til 4 timer.
                  </p>

                  <div className="example-box">
                    <h4>Real-world eksempel: IBM Carbon</h4>
                    <p>
                      Over et år sparede IBM's udviklingsteam over 12.000 timer med Carbon Design System. 
                      Det svarer til 6 fuldtids-medarbejdere der kunne fokusere på innovation i stedet 
                      for at genopfinde hjulet.
                    </p>
                  </div>

                  <h3>2. Konsistens På Tværs af Platforme</h3>
                  <p>
                    Moderne brugere møder dit brand på hjemmesider, mobile apps, emails, marketing materials. 
                    Et designsystem sikrer at en primary button ser identisk ud på iOS, Android og web. 
                    Denne konsistens er ikke bare æstetisk - det er psykologisk. Forskning viser at 
                    konsistente interfaces øger user trust med 33% og reducerer cognitive load markant.
                  </p>

                  <h3>3. Skalerbarhed for Voksende Teams</h3>
                  <p>
                    Når dit team vokser fra 2 til 20 designere, bliver kaos uundgåeligt uden centraliserede 
                    standarder. Hver designer implementerer knapper lidt forskelligt. Før du ved af det, 
                    har du 47 variationer af samme komponent. Med et designsystem onboarder nye 
                    teammedlemmer 68% hurtigere (Google's data).
                  </p>

                  <h3>4. Accessibility Bygget Ind Fra Start</h3>
                  <p>
                    At bygge tilgængelighed ind i hver komponent én gang er eksponentielt mere effektivt 
                    end at skulle fixe det i hundredvis af implementeringer. Gov.uk's designsystem sikrer 
                    WCAG 2.1 AA compliance som default - ikke som eftertanke. Når accessibility er baked in, 
                    kan teams fokusere på features i stedet for remediation.
                  </p>

                  <h3>5. Reduceret Technical Debt</h3>
                  <p>
                    Uden et designsystem akkumulerer "design debt" hurtigt. Inkonsistente komponenter, 
                    dubleret kode, forældede mønstre. Cleanups koster dyrt. Shopify's Polaris designsystem 
                    hjalp dem med at eliminere 60% af deres CSS og konsolidere 300+ komponenter ned til 
                    47 veldefinerede, vedligeholdte byggeklodser.
                  </p>

                  <div className="highlight-box">
                    <h4>Hvornår skal du IKKE bygge et designsystem?</h4>
                    <p>
                      Lad os være realistiske - designsystemer er ikke for alle. Hvis du er:
                    </p>
                    <ul>
                      <li>En lille startup med 1-2 produkter og intet plan om at skalere</li>
                      <li>I en meget eksperimentel fase hvor alt konstant ændrer sig</li>
                      <li>Mangler dedikerede ressourcer til at vedligeholde systemet</li>
                    </ul>
                    <p>
                      ...så er dit tidspunkt måske ikke nu. Start med et simpelt komponentbibliotek og 
                      evoluer til et fuldt designsystem når behovet opstår. Det vigtigste er at erkende 
                      at et designsystem kræver commitment.
                    </p>
                  </div>
                </div>
              </section>

              {/* Komponenter */}
              <section className="artikel-section" id="komponenter">
                <h2>Kernekomponenter i et Moderne Designsystem</h2>
                
                <div className="artikel-text">
                  <p>
                    Lad os dykke dybt ned i de konkrete dele et professionelt designsystem består af. 
                    Dette er ikke teoretisk - det er battle-tested knowledge fra implementeringer hos 
                    virksomheder som Google, Atlassian og Salesforce.
                  </p>

                  <h3>Design Tokens - Systemets DNA</h3>
                  <p>
                    Design tokens er de mindste, mest fundamentale byggeklodser. Tænk på dem som 
                    systemets DNA - de bærer den genetiske information der definerer hvordan alt ser ud.
                    Tokens gør det muligt at maintaine konsistens og faciliterer temaer (light/dark mode).
                  </p>

                  <h4>Farve-tokens: Semantisk Navngivning</h4>
                  <p>
                    En af de mest kritiske beslutninger er hvordan du navngiver dine farver. Undgå at 
                    navngive efter appearance - brug semantiske, purpose-driven navne:
                  </p>

                  <div className="code-example">
                    <h5>❌ Dårlig praksis - appearance-based navngivning:</h5>
                    <pre><code>{`--blue-500: #3B82F6;
--red-600: #DC2626;
--gray-200: #E5E7EB;`}</code></pre>

                    <h5>✅ God praksis - semantisk navngivning:</h5>
                    <pre><code>{`--color-primary: #3B82F6;
--color-danger: #DC2626;
--color-surface: #FFFFFF;
--color-text-primary: #1F2937;
--color-text-secondary: #6B7280;
--color-border: #E5E7EB;`}</code></pre>
                  </div>

                  <p>
                    <strong>Hvorfor semantisk?</strong> Når du skal rebrand fra blå til grøn, skal du kun 
                    ændre ét sted. <code>--color-primary</code> kan pege på enhver farve uden at bryde 
                    semantikken. Med appearance-based navne ville <code>--blue-500</code> skulle renames 
                    til <code>--green-500</code> overalt.
                  </p>

                  <h4>Spacing Tokens: The 8-Point Grid System</h4>
                  <p>
                    De fleste professionelle designsystemer bruger en spacing scale baseret på 8px 
                    increments. Hvorfor 8? Fordi det er divisible med 2 og 4, matcher de fleste 
                    skærmstørrelser perfekt, og skaber en naturlig rytme.
                  </p>

                  <div className="code-example">
                    <pre><code>{`--spacing-xs: 4px;   // 0.5 × base
--spacing-sm: 8px;   // 1 × base  
--spacing-md: 16px;  // 2 × base
--spacing-lg: 24px;  // 3 × base
--spacing-xl: 32px;  // 4 × base
--spacing-2xl: 48px; // 6 × base
--spacing-3xl: 64px; // 8 × base`}</code></pre>
                  </div>

                  <p>
                    Dette spacing system gør det muligt at skabe konsistent vertical rhythm og 
                    forudsigeligt layout. Designers kan komponere interfaces ved at "snakke i tokens" 
                    i stedet for pixel-pushing.
                  </p>

                  <h4>Typografi Tokens: Harmonisk Type Scale</h4>
                  <p>
                    En velstruktureret typografisk skala skaber visuelt hierarki uden at kræve custom 
                    størrelser hver gang. Material Design's modular scale er et excellent eksempel:
                  </p>

                  <div className="code-example">
                    <pre><code>{`--font-size-xs: 0.75rem;   // 12px - Captions, labels
--font-size-sm: 0.875rem;  // 14px - Body small
--font-size-base: 1rem;    // 16px - Body text
--font-size-lg: 1.125rem;  // 18px - Large body
--font-size-xl: 1.25rem;   // 20px - H5
--font-size-2xl: 1.5rem;   // 24px - H4
--font-size-3xl: 1.875rem; // 30px - H3
--font-size-4xl: 2.25rem;  // 36px - H2
--font-size-5xl: 3rem;     // 48px - H1, Hero`}</code></pre>
                  </div>

                  <h3>Komponentbibliotek - De Genbrugelige Byggeklodser</h3>
                  <p>
                    Med design tokens defineret, kan vi bygge komponenter der bruger dem konsistent. 
                    Hvert komponent skal tænkes igennem metodisk - ikke bare designet, men architected.
                  </p>

                  <h4>Button Component Anatomy</h4>
                  <p>
                    Lad os dissekere en professionel button komponent. Dette er ikke bare "en knap" - 
                    det er et fuldt gennemtænkt system med variations, states og sizes:
                  </p>

                  <div className="component-anatomy">
                    <h5>Button Variants (Visual Hierarchy):</h5>
                    <ul className="simple-list">
                      <li><strong>Primary:</strong> Hovedhandling på siden (kun én per view)</li>
                      <li><strong>Secondary:</strong> Alternative handlinger af medium prioritet</li>
                      <li><strong>Tertiary:</strong> Lavprioritet handlinger</li>
                      <li><strong>Ghost:</strong> Meget lavprioritet, ofte i sidebars eller footers</li>
                      <li><strong>Danger:</strong> Destruktive handlinger (slet, annuller) med warning signaling</li>
                    </ul>

                    <h5>Button States (Interaction Feedback):</h5>
                    <ul className="simple-list">
                      <li><strong>Default:</strong> Normal resting state</li>
                      <li><strong>Hover:</strong> Cursor over button, subtle color shift</li>
                      <li><strong>Active:</strong> Mens der klikkes, slight scale eller shadow change</li>
                      <li><strong>Focus:</strong> Keyboard navigation, visible outline ring</li>
                      <li><strong>Disabled:</strong> Ikke-interaktiv, reduced opacity + cursor: not-allowed</li>
                      <li><strong>Loading:</strong> Async operation i gang, spinner + disabled state</li>
                    </ul>

                    <h5>Button Sizes (Context Appropriate):</h5>
                    <ul className="simple-list">
                      <li><strong>Small:</strong> 32px height - Compact UIs, tables, cards</li>
                      <li><strong>Medium:</strong> 40px height - Standard størrelse (default)</li>
                      <li><strong>Large:</strong> 48px height - Hero sections, landing pages, primary CTAs</li>
                    </ul>
                  </div>

                  <p>
                    <strong>Den eksponentielle kompleksitet:</strong> En single button komponent med 
                    5 variants × 6 states × 3 sizes = 90 mulige kombinationer. Uden et designsystem 
                    skulle hver implementeres og testes individuelt. Med et system? Ét sted, én gang, 
                    perfekt konsistent.
                  </p>

                  <h3>Documentation - Det Levende Bibliotek</h3>
                  <p>
                    Et designsystem uden god dokumentation er som et bibliotek uden katalog. Du har 
                    ressourcerne, men kan ikke finde dem. Documentation er ofte den mest undervurderede 
                    del af et designsystem, men også den mest kritiske for adoption.
                  </p>

                  <h4>Hvad skal dokumenteres?</h4>
                  <p>Comprehensive documentation dækker mindst disse områder:</p>
                  <ul className="simple-list">
                    <li><strong>Komponent API:</strong> Hvilke props accepteres? Hvad er required vs optional? Default values?</li>
                    <li><strong>Use cases:</strong> Hvornår bruges komponenten? Lige så vigtigt: Hvornår bruges den IKKE?</li>
                    <li><strong>Accessibility:</strong> Keyboard navigation, ARIA labels, screen reader support, focus management</li>
                    <li><strong>Code examples:</strong> Funktionelle eksempler i alle supported frameworks (React, Vue, Angular)</li>
                    <li><strong>Design rationale:</strong> Hvorfor er decisions truffet? Hvilket problem løser komponenten?</li>
                    <li><strong>Visual specs:</strong> Spacing, typography, colors med inspect-able values</li>
                  </ul>

                  <div className="tool-recommendation">
                    <h4>Anbefalede Documentation Tools:</h4>
                    <ul className="simple-list">
                      <li><strong>Storybook:</strong> Industry standard for komponent documentation, excellent for developers</li>
                      <li><strong>Zeroheight:</strong> Designer-friendly, ingen kode påkrævet, syncer med Figma</li>
                      <li><strong>Notion:</strong> Fleksibelt for guidelines, principper og onboarding docs</li>
                      <li><strong>Custom site:</strong> Next.js/Gatsby site med MDX for fuld control</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Opbygning af designsystem */}
              <section className="artikel-section" id="opbygning">
                <h2>Opbygning af et Designsystem - Fra Bunden til Lancering</h2>
                
                <div className="artikel-text">
                  <p>
                    At bygge et designsystem kan virke overvældende, men med den rigtige tilgang kan 
                    selv små teams skabe værdi hurtigt. Her er en velafprøvet ramme for at komme i gang.
                  </p>

                  <h3>Fase 1: Kortlægning og Gennemgang</h3>
                  <p>
                    Før du bygger noget nyt, skal du forstå hvad du allerede har. Foretag en grundig 
                    designgennemgang af dine eksisterende produkter:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Komponentoversigt:</strong> Tag skærmbilleder af alle unikke komponenter (knapper, inputfelter, kort, osv.)</li>
                    <li><strong>Farvegennemgang:</strong> Udtræk alle farver brugt i produktet - du vil blive overrasket over hvor mange variationer der er</li>
                    <li><strong>Typografi gennemgang:</strong> Dokumenter alle skriftstørrelser, vægte og linjehøjder</li>
                    <li><strong>Afstandsinkonsistenser:</strong> Identificer hvor mellemrum varierer unødvendigt</li>
                  </ul>

                  <p>
                    Denne gennemgang afslører ofte kaos du ikke vidste eksisterede. Det er normalt at finde 
                    30+ knapvariationer, 50+ forskellige farver, og 20+ skriftstørrelser. Dette kaos er 
                    præcis hvorfor du behøver et designsystem.
                  </p>

                  <h3>Fase 2: Definer Designprincipper</h3>
                  <p>
                    Designprincipper er dit kompas - de guider alle beslutninger. Gode principper er:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Specifikke:</strong> Ikke generiske udsagn som "vi værdsætter brugerne"</li>
                    <li><strong>Handlingsorienterede:</strong> De skal kunne guide konkrete designbeslutninger</li>
                    <li><strong>Mindeværdige:</strong> Hold det til 3-5 principper folk faktisk husker</li>
                  </ul>

                  <div className="example-box">
                    <h4>Eksempel - Airbnb's Designprincipper:</h4>
                    <ul className="simple-list">
                      <li><strong>Forenet:</strong> Skab et forenet, inkluderende fællesskab</li>
                      <li><strong>Universelt:</strong> Tilgængeligt for alle, uanset evner</li>
                      <li><strong>Ikonisk:</strong> Mindeværdigt og øjeblikkeligt genkendeligt</li>
                      <li><strong>Samtalende:</strong> Naturlig og menneskelig kommunikation</li>
                    </ul>
                  </div>

                  <h3>Fase 3: Start Med Fundamentet</h3>
                  <p>
                    Byg dit fundamentlag først - designtokens for farver, typografi og mellemrum. 
                    Dette er det mest kritiske lag fordi alt andet bygger ovenpå det.
                  </p>

                  <h3>Fase 4: Byg Kernekomponenter</h3>
                  <p>
                    Start ikke med at bygge alle 100 komponenter. Fokuser på de "store 5":
                  </p>
                  <ul className="simple-list">
                    <li><strong>Knapper:</strong> Mest brugte interaktive element</li>
                    <li><strong>Inputfelter:</strong> Kritisk for alle formularer</li>
                    <li><strong>Typografi:</strong> Overskrifts- og brødtekststile</li>
                    <li><strong>Kort:</strong> Beholder for grupperet indhold</li>
                    <li><strong>Navigation:</strong> Header/footer komponenter</li>
                  </ul>

                  <p>
                    Disse 5 komponenter dækker 80% af de fleste brugergrænseflader. Når de er solide, udvid iterativt.
                  </p>

                  <h3>Fase 5: Dokumentation og Lancering</h3>
                  <p>
                    Et designsystem uden dokumentation er ubrugeligt. Prioriter omfattende dokumentation fra dag ét. 
                    Inkluder kodeeksempler, anvendelsestilfælde og tilgængelighedsretningslinjer for hver komponent.
                  </p>
                </div>
              </section>

              {/* Best Practices */}
              <section className="artikel-section" id="best-practices">
                <h2>Bedste Praksis - Læringer Fra Branchen</h2>
                
                <div className="artikel-text">
                  <p>
                    Efter at have analyseret designsystemer fra Google, Shopify, Atlassian, og andre 
                    brancheledere, her er de mest indflydelsesrige bedste praksisser:
                  </p>

                  <h3>1. Behandl Dit Designsystem Som et Produkt</h3>
                  <p>
                    De mest succesfulde designsystemer behandles som selvstændige produkter med dedikeret 
                    team, køreplan og bruger-feedback-loops. Din "bruger" er andre designere og udviklere.
                  </p>
                  <ul className="simple-list">
                    <li><strong>Produktansvarlig:</strong> Nogen ejer prioritering og køreplan</li>
                    <li><strong>Supportkanal:</strong> Slack kanal eller forum for spørgsmål</li>
                    <li><strong>Udgivelsesnoter:</strong> Kommuniker ændringer tydeligt</li>
                    <li><strong>Bidragsproces:</strong> Gør det nemt at bidrage tilbage</li>
                  </ul>

                  <h3>2. Versionér Alt</h3>
                  <p>
                    Brug semantisk versionering (hoved.mindre.rettelse) så teams kan adoptere opdateringer trygt:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Hoved (1.0.0 → 2.0.0):</strong> Brudændringer - kræver kodeopdateringer</li>
                    <li><strong>Mindre (1.0.0 → 1.1.0):</strong> Nye funktioner - bagudkompatibel</li>
                    <li><strong>Rettelse (1.0.0 → 1.0.1):</strong> Fejlrettelser - ingen funktionelle ændringer</li>
                  </ul>

                  <h3>3. Byg Tilgængelighed Ind, Ikke På</h3>
                  <p>
                    Tilgængelighed skal ikke være en eftertanke. Hver komponent skal være WCAG 2.1 AA 
                    kompatibel fra dag ét:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Farvekontrast:</strong> Minimum 4.5:1 for normal tekst, 3:1 for stor tekst</li>
                    <li><strong>Tastaturnavigation:</strong> Alle interaktive elementer tilgængelige via tastatur</li>
                    <li><strong>Skærmlæserunderstøttelse:</strong> Korrekte ARIA-labels og semantisk HTML</li>
                    <li><strong>Fokusindikatorer:</strong> Synlig fokustilstand for tastaturbrugere</li>
                  </ul>

                  <h3>4. Automatiser Testning</h3>
                  <p>
                    Manuel testning skalerer ikke. Automatiser så meget som muligt:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Visuel regression:</strong> Chromatic eller Percy for at fange utilsigtede ændringer</li>
                    <li><strong>Tilgængelighedstestning:</strong> axe-core integration i CI/CD</li>
                    <li><strong>Enhedstests:</strong> Test komponentlogik og kanttilfælde</li>
                    <li><strong>Integrationstests:</strong> Test komponenter sammen i realistiske scenarier</li>
                  </ul>

                  <h3>5. Skab Klare Bidragsretningslinjer</h3>
                  <p>
                    Et levende designsystem vokser gennem bidrag. Gør det nemt:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Anmodningsproces:</strong> Anmodning om kommentarer for større tilføjelser</li>
                    <li><strong>Komponentforslagsskabelon:</strong> Standardiseret format for nye komponenter</li>
                    <li><strong>Designgennemgang:</strong> Klar godkendelsesproces og kriterier</li>
                    <li><strong>Kodegennemgang:</strong> Konsistens i implementeringsstandarder</li>
                  </ul>

                  <div className="tool-recommendation">
                    <h4>💡 Professionelt Tip: 80/20 Reglen</h4>
                    <p>
                      Fokuser på at bygge komponenter der bruges af 80% af dine produkter. Specialiserede 
                      komponenter kan leve udenfor designsystemet indtil de beviser deres værdi.
                    </p>
                  </div>
                </div>
              </section>

              {/* Kendte Eksempler */}
              <section className="artikel-section" id="eksempler">
                <h2>Kendte Eksempler - Lær Fra de Bedste</h2>
                
                <div className="artikel-text">
                  <p>
                    Lad os analysere nogle af verdens mest succesfulde designsystemer og hvad vi kan 
                    lære fra dem:
                  </p>

                  <h3>Google Material Design</h3>
                  <p>
                    Måske verdens mest kendte designsystem. Material Design's styrke er omfattende 
                    retningslinjer der dækker alt fra bevægelse til ikonografi:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Styrke:</strong> Ekstremt omfattende dokumentation med interaktive eksempler</li>
                    <li><strong>Styrke:</strong> Platformoverskridende (Android, iOS, Web, Flutter)</li>
                    <li><strong>Læring:</strong> Detaljerede bevægelsesretningslinjer skaber konsekvente animationer</li>
                    <li><strong>Læring:</strong> Eleveringssystem (skygger) skaber klar visuel hierarki</li>
                  </ul>

                  <h3>Shopify Polaris</h3>
                  <p>
                    Bygget specifikt til e-handels admingrænseflader, Polaris er et fremragende eksempel 
                    på et fokuseret designsystem:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Styrke:</strong> Ekstremt fokus på køb mandsarbejdsgange og anvendelsestilfælde</li>
                    <li><strong>Styrke:</strong> Fremragende tilgængelighed - bygget til tastatur-tungt administrationsarbejde</li>
                    <li><strong>Læring:</strong> Specialbyggede komponenter til almindelige e-handelsmønstre</li>
                    <li><strong>Læring:</strong> Klar vejledning om hvornår forskellige komponenter bruges</li>
                  </ul>

                  <h3>IBM Carbon Design System</h3>
                  <p>
                    Designsystem af virksomhedsklasse der balancerer fleksibilitet med konsistens:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Styrke:</strong> Tematiseringssystem der tillader brandtilpasning</li>
                    <li><strong>Styrke:</strong> Datavisualiseringskomponenter til virksomhedsdashboards</li>
                    <li><strong>Læring:</strong> Gitternetssystem der skalerer fra mobil til store skærme</li>
                    <li><strong>Læring:</strong> Omfattende udviklerfokuseret dokumentation</li>
                  </ul>

                  <h3>Atlassian Design System</h3>
                  <p>
                    Driver Jira, Confluence, Trello og andre Atlassian-produkter:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Styrke:</strong> Fokus på samarbejds- og kommunikationsmønstre</li>
                    <li><strong>Styrke:</strong> Fremragende tomme tilstande og onboarding-forløb</li>
                    <li><strong>Læring:</strong> Kontekstbevidste komponenter der tilpasser sig brugerens arbejdsgang</li>
                    <li><strong>Læring:</strong> Stærkt fokus på progressiv afsløring - vis kompleksitet når nødvendigt</li>
                  </ul>

                  <div className="example-box">
                    <h4>Fælles Træk Ved Succesfulde Designsystemer:</h4>
                    <ul className="simple-list">
                      <li>Omfattende, søgbar dokumentation</li>
                      <li>Live kodeeksempler du kan kopiere/indsætte</li>
                      <li>Klare tilgængelighedsretningslinjer</li>
                      <li>Aktiv vedligeholdelse og regelmæssige opdateringer</li>
                      <li>Fællesskabs- eller supportkanaler</li>
                      <li>Versioneringsstrategi med migrationsvejledninger</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Implementering */}
              <section className="artikel-section" id="implementering">
                <h2>Implementering - Fra Design til Kode</h2>
                
                <div className="artikel-text">
                  <p>
                    Et designsystem skal leve i både designværktøjer og kode. Her er hvordan du bygger 
                    bro mellem de to effektivt.
                  </p>

                  <h3>Designværktøjsopsætning - Figma</h3>
                  <p>
                    Figma er blevet de facto standarden for designsystemer takket være komponenter, 
                    varianter og auto-layout:
                  </p>

                  <h4>Opbygning i Figma:</h4>
                  <ul className="simple-list">
                    <li><strong>Separat biblioteksfil:</strong> Hold designsystemet i dedikeret fil</li>
                    <li><strong>Komponentvarianter:</strong> Brug varianter til tilstande og størrelser</li>
                    <li><strong>Auto-layout:</strong> Gør komponenter responsive og tilpasningsdygtige</li>
                    <li><strong>Stilarter:</strong> Definer farve-, typografi- og effektstilarter</li>
                    <li><strong>Dokumentation:</strong> Brug beskrivelsesfelter til at dokumentere anvendelse</li>
                  </ul>

                  <h3>Kodeimplementering</h3>
                  <p>
                    Dit valg af teknologistak afhænger af dit team og produkter, men principperne er de samme:
                  </p>

                  <h4>Rammeværk-Agnostisk Tilgang:</h4>
                  <p>
                    Hvis du supporterer flere rammeværker (React, Vue, Angular), overvej webkomponenter 
                    som basislag. Alternativt, vedligehold separate implementeringer med delte designtokens.
                  </p>

                  <div className="code-example">
                    <h5>Eksempel struktur:</h5>
                    <pre><code>{`design-system/
├── tokens/          # Designtokens (JSON, JS)
│   ├── colors.json
│   ├── typography.json
│   └── spacing.json
├── components/      # Rammeværk-specifikke komponenter
│   ├── react/
│   ├── vue/
│   └── web-components/
├── docs/           # Dokumentationssite
└── tooling/        # Bygge- og udviklingsværktøjer`}</code></pre>
                  </div>

                  <h3>Token-transformation</h3>
                  <p>
                    Designtokens skal transformeres til forskellige formater for forskellige platforme. 
                    Style Dictionary fra Amazon er branchestandarden til dette:
                  </p>

                  <div className="code-example">
                    <h5>Samme token, forskellige outputformater:</h5>
                    <pre><code>{`// tokens/colors.json
{
  "color": {
    "brand": {
      "primary": { "value": "#0066CC" }
    }
  }
}

// Output: CSS
:root {
  --color-brand-primary: #0066CC;
}

// Output: JavaScript
export const colorBrandPrimary = '#0066CC';

// Output: iOS (Swift)
let colorBrandPrimary = UIColor(hex: "0066CC")

// Output: Android (XML)
<color name="color_brand_primary">#0066CC</color>`}</code></pre>
                  </div>

                  <h3>Distributionsstrategi</h3>
                  <p>
                    Hvordan du distribuerer dit designsystem afhænger af din organisation:
                  </p>

                  <h4>Muligheder:</h4>
                  <ul className="simple-list">
                    <li><strong>NPM-pakke:</strong> Standard for JavaScript-økosystemet - nem versionsstyring</li>
                    <li><strong>Monorepo:</strong> Alle produkter og designsystemet i samme kodebase</li>
                    <li><strong>CDN:</strong> Vært for CSS/JS filer til hurtig adgang uden installation</li>
                    <li><strong>Git submodul:</strong> Traditionel tilgang, men kan være besværlig at vedligeholde</li>
                  </ul>

                  <div className="tool-recommendation">
                    <h4>🔧 Anbefalede Værktøjer til Implementering:</h4>
                    <ul className="simple-list">
                      <li><strong>Storybook:</strong> Til komponentudvikling og dokumentation</li>
                      <li><strong>Style Dictionary:</strong> Til token-transformation</li>
                      <li><strong>Chromatic:</strong> Til visuel regressionstestning</li>
                      <li><strong>Figma API:</strong> Til synkronisering af design og kode</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Vedligeholdelse */}
              <section className="artikel-section" id="vedligeholdelse">
                <h2>Vedligeholdelse og Skalering Over Tid</h2>
                
                <div className="artikel-text">
                  <p>
                    At lancere et designsystem er kun begyndelsen. Det rigtige arbejde er kontinuerlig 
                    vedligeholdelse, udvikling og adoption.
                  </p>

                  <h3>Styringsmodel</h3>
                  <p>
                    Hvem beslutter hvad der kommer ind i designsystemet? Du behøver klar styring:
                  </p>

                  <h4>Centraliseret Model:</h4>
                  <p>
                    Et dedikeret team ejer og vedligeholder systemet. Andre teams kan bidrage, men 
                    kerneteamet har det endelige ord.
                  </p>
                  <ul className="simple-list">
                    <li><strong>Fordel:</strong> Høj konsistens og kvalitetskontrol</li>
                    <li><strong>Fordel:</strong> Klart ejerskab og ansvarlighed</li>
                    <li><strong>Ulempe:</strong> Kan blive en flaskehals for innovation</li>
                    <li><strong>Ulempe:</strong> Kræver dedikerede ressourcer</li>
                  </ul>

                  <h4>Fødereret Model:</h4>
                  <p>
                    Flere teams bidrager til og vedligeholder forskellige dele af systemet. Kerneteamet 
                    sætter standarder og gennemgår ændringer.
                  </p>
                  <ul className="simple-list">
                    <li><strong>Fordel:</strong> Skalerer bedre for store organisationer</li>
                    <li><strong>Fordel:</strong> Fordeler vedligeholdelsesbyrden</li>
                    <li><strong>Ulempe:</strong> Kræver stærke retningslinjer og gennemgangsproces</li>
                    <li><strong>Ulempe:</strong> Risiko for inkonsistens uden ordentligt tilsyn</li>
                  </ul>

                  <h3>Måling af Succes</h3>
                  <p>
                    Følg målinger der viser dit designsystems indvirkning:
                  </p>

                  <h4>Adoptionsmålinger:</h4>
                  <ul className="simple-list">
                    <li><strong>Komponentanvendelse:</strong> Hvor mange teams bruger hvilke komponenter?</li>
                    <li><strong>Dækning:</strong> Procentdel af produktet bygget med designsystemkomponenter</li>
                    <li><strong>Tid til markedet:</strong> Hvor hurtigt kan nye funktioner bygges?</li>
                  </ul>

                  <h4>Kvalitetsmålinger:</h4>
                  <ul className="simple-list">
                    <li><strong>Tilgængelighedsoverholdelse:</strong> WCAG-overtrædelser over tid</li>
                    <li><strong>Fejlrater:</strong> Problemer rapporteret per komponent</li>
                    <li><strong>Ydeevne:</strong> Indlæsningstider og pakkestørrelser</li>
                  </ul>

                  <h4>Udviklererfaringsmålinger:</h4>
                  <ul className="simple-list">
                    <li><strong>Dokumentationsvisninger:</strong> Hvilke sider er mest besøgt?</li>
                    <li><strong>Supportanmodninger:</strong> Hvor får folk problemer?</li>
                    <li><strong>Tilfredshedsundersøgelser:</strong> Regelmæssig feedback fra brugere</li>
                  </ul>

                  <h3>Udviklingsstrategi</h3>
                  <p>
                    Designsystemer skal udvikle sig med dine produkter og brugerbehov:
                  </p>

                  <h4>Kvartalsvise Gennemgange:</h4>
                  <p>
                    Gennemgå adoptionsdata, brugerfeedback og branchetendenser. Prioriter køreplan baseret 
                    på forretningsindvirkning.
                  </p>

                  <h4>Udfasningsproces:</h4>
                  <p>
                    Når komponenter skal udfases:
                  </p>
                  <ul className="simple-list">
                    <li><strong>1. Annoncer:</strong> Klar kommunikation om udfasning 3-6 måneder før</li>
                    <li><strong>2. Migrationsvejledning:</strong> Detaljerede instruktioner til flytning til erstatning</li>
                    <li><strong>3. Supportperiode:</strong> Ret kritiske fejl men ingen nye funktioner</li>
                    <li><strong>4. Fjern:</strong> Efter frist, fjern fra systemet med større versionsopdatering</li>
                  </ul>

                  <div className="key-takeaways">
                    <h3>Bedste Praksis for Vedligeholdelse:</h3>
                    <ul>
                      <li><strong>Regelmæssige udgivelser:</strong> Levér opdateringer forudsigeligt (f.eks. hver anden uge)</li>
                      <li><strong>Ændringsdisciplin:</strong> Detaljerede udgivelsesnoter for hver version</li>
                      <li><strong>Budget for brudændringer:</strong> Begræns brudændringer til maks. X per år</li>
                      <li><strong>Åbningstider:</strong> Regelmæssige sessioner hvor teams kan få hjælp</li>
                      <li><strong>Vis og fortæl:</strong> Månedlige demonstrationer af nye komponenter og funktioner</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Konklusion */}
              <section className="artikel-section" id="konklusion">
                <h2>Konklusion: Designsystemer som Strategisk Investering</h2>
                
                <div className="artikel-text">
                  <p>
                    At bygge et designsystem er ikke bare en teknisk opgave - det er en strategisk 
                    forretningsbeslutning med målbar ROI. Virksomheder der investerer i velstrukturerede 
                    designsystemer ser dokumenterede forbedringer i udviklingshastighed, produktkvalitet, 
                    team collaboration og ultimativt brugeroplevelse.
                  </p>

                  <p>
                    Som vi har set gennem denne guide, handler designsystemer om meget mere end pæne 
                    komponenter. Det handler om at skabe et fælles sprog mellem design og development, 
                    reducere friktion mellem teams og bygge produkter der skalerer elegant uden at 
                    akkumulere technical debt.
                  </p>

                  <div className="key-takeaways">
                    <h3>Vigtigste Takeaways:</h3>
                    <ul>
                      <li><strong>Start småt:</strong> Du behøver ikke alt fra dag ét. Begin with foundations og byg iterativt</li>
                      <li><strong>Involvement:</strong> Involver alle stakeholders tidligt - designers, developers, product managers</li>
                      <li><strong>Documentation:</strong> Det er ikke optional, det er critical. Ingen adoption uden god docs</li>
                      <li><strong>Governance:</strong> Definer ejerskab, versioning strategy og contribution guidelines</li>
                      <li><strong>Måling:</strong> Track adoption metrics og business impact for at vise værdi</li>
                      <li><strong>Evolution:</strong> De bedste designsystemer er aldrig "færdige" - de er living products</li>
                    </ul>
                  </div>

                  <p>
                    Husk: Implementering af et designsystem er en marathon, ikke et sprint. Men med 
                    tålmodighed, dedikation og de rigtige værktøjer, vil du se transformative resultater. 
                    Start i dag med at definere dine design tokens. Det er det første skridt på en rejse 
                    mod skalerbar, konsistent og maintainable design.
                  </p>
                </div>
              </section>

              {/* Relateret Indhold */}
              <section className="related-content-section">
                <h3>Læs også disse artikler</h3>
                <div className="related-articles">
                  <div className="related-article">
                    <h4><a href="/artikler/webdesign-trends-2025">Webdesign Trends 2025</a></h4>
                    <p>Opdag de nyeste trends i moderne webdesign og lær hvordan du skaber hjemmesider der konverterer.</p>
                  </div>
                  
                  <div className="related-article">
                    <h4><a href="/artikler/minimalistisk-webdesign">Minimalistisk Webdesign</a></h4>
                    <p>Lær kunsten i at designe med mindre og skab kraftfulde hjemmesider gennem fokuseret, clean design.</p>
                  </div>
                  
                  <div className="related-article">
                    <h4><a href="/artikler/mobilvenlige-hjemmesider">Mobilvenlige Hjemmesider</a></h4>
                    <p>Best practices for mobile-first design og hvorfor din virksomhed skal være optimeret til mobil.</p>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default Designsystemer;
